import { Icon } from "@/components/icons/Icon"
import { KeeperIcon } from "@/components/icons/CullingIcon"
import { useTranslation } from "react-i18next"
import { useAtomValue, useSetAtom } from "jotai"
import clsx from "clsx"
import { Spinner } from "@/components/ui/Spinner"
import { selectCluster, selectPhoto } from "@/store/photo/photos-selection-state"
import { RatingValues, type PhotoRecord, type Rating } from "@/lib/catalog/types"
import { useThumbnailPreview } from "@/store/previews/previews-processing"
import { PhotoContextMenu } from "@/components/PhotoContextMenu"
import { showContextMenu } from "@/components/ContextMenuManager"
import { openFocusView, photosViewAtom } from "@/store/photo/photos-view-state"
import { analytics } from "@/telemetry/amplitude"
import React, { useEffect, useRef, useState } from "react"
import { ProjectItem } from "@/store/project/projects-service"
import {
	clusterPhotoGroupInfoMapAtom,
	ClusterInfo,
	currentClusterAtom,
	getCullingTag,
	groupSimilarPhotosAtom,
	hoveredGroupedClusterIdAtom,
	isSuggestingKeepersAtom,
	isSuggestingKeepersOrRejectsAtom,
	selectedGroupedClusterLeaderIdsAtom,
} from "@/store/importing/culling-state"
import { ratePhoto, rateSelectedPhotos, unhidePhotos } from "@/store/photo/store-photos"
import {
	currentPhotoAtom,
	aiDebugBorderAtom,
	photosAtom,
	selectedPhotosIDsAtom,
} from "@/store/photo/photos-state"
import { store } from "@/store/store"
import {
	isCurrentFileLoaded,
	loadCurrentFile,
	syncAdjustmentHistory,
} from "@/store/editor/photo-loading"
import { MemoizedThumbnailFlag } from "./ThumbnailFlag"
import { MemoizedThumbnailColorSetter } from "./ThumbnailColorSetter"
import { MemoizedCullingTagIconSetter } from "@/components/CullingTagSetter"
import { isCullingModeAtom, isManualModeAtom } from "@/store/project/projects-state"
import { useIsPrimarySelection, useIsSecondarySelection } from "./useSelection"
import { PreviewSize } from "@/store/previews/previews-types"
import { isRatingBarActiveAtom } from "@/store/ui-views-state"
import { middleTruncate } from "@/utils/string"
import { selectThumbnailPhotoFromClick } from "./thumbnail-selection"

export type ThumbnailSize = "small" | "medium"

const thumbnailBackgroundClassesByColorTag = {
	// i18n-ignore-start: ColorTag enum keys used as object keys
	"No Color": {
		base: "bg-base-900",
		hover: "bg-base-700",
		selected: "bg-base-600",
	},
	Red: {
		base: "bg-[#917475]",
		hover: "bg-[#917475]/60",
		selected: "bg-[#917475]/70",
	},
	Green: {
		base: "bg-[#758A75]",
		hover: "bg-[#758A75]/60",
		selected: "bg-[#758A75]/70",
	},
	Yellow: {
		base: "bg-[#90916E]",
		hover: "bg-[#90916E]/60",
		selected: "bg-[#90916E]/70",
	},
	Blue: {
		base: "bg-[#747D91]",
		hover: "bg-[#747D91]/60",
		selected: "bg-[#747D91]/70",
	},
	Purple: {
		base: "bg-[#7E7492]",
		hover: "bg-[#7E7492]/60",
		selected: "bg-[#7E7492]/70",
	},
} as const
// i18n-ignore-end

export const Thumbnail = (props: {
	size: ThumbnailSize
	photo: PhotoRecord
	project: ProjectItem
	isStackedPhoto: boolean
}) => {
	const [singleTapTimeout, setSingleTapTimeout] = useState<NodeJS.Timeout | null>(null)
	const photo = props.photo

	const isManualMode = useAtomValue(isManualModeAtom)
	const isCullingMode = useAtomValue(isCullingModeAtom)
	const isSuggestingKeepersOrRejects = useAtomValue(isSuggestingKeepersOrRejectsAtom)
	const groupSimilarPhotos = useAtomValue(groupSimilarPhotosAtom)
	const clusterPhotoGroupInfoMap = useAtomValue(clusterPhotoGroupInfoMapAtom)
	const selectedGroupedClusterLeaderIds = useAtomValue(selectedGroupedClusterLeaderIdsAtom)
	const setIsRatingBarActive = useSetAtom(isRatingBarActiveAtom)
	const setHoveredGroupedClusterId = useSetAtom(hoveredGroupedClusterIdAtom)
	const hoveredGroupedClusterId = useAtomValue(hoveredGroupedClusterIdAtom)
	const currentPhoto = useAtomValue(currentPhotoAtom)

	const mode = useAtomValue(photosViewAtom)

	const isPrimarySelection = useIsPrimarySelection(photo.id)
	const isSecondarySelection = useIsSecondarySelection(photo.id)

	const isSelected = isPrimarySelection || isSecondarySelection
	const [isHovering, setIsHovering] = useState(false)
	const [isMenuOpen, setIsMenuOpen] = useState(false)

	const thumbnailRef = useRef<HTMLDivElement>(null)
	const imageRef = useRef<HTMLImageElement>(null)
	const [thumbnailSize, setThumbnailSize] = useState<number | null>(null)
	const lastThumbnailSizeRef = useRef<number | null>(null)
	const [imageNaturalSize, setImageNaturalSize] = useState<{
		width: number
		height: number
	} | null>(null)

	const shouldHandleRatingBar = mode !== "grid" && currentPhoto?.id === photo.id

	useEffect(() => {
		const element = thumbnailRef.current
		if (!element) return

		const updateThumbnailSize = () => {
			const rect = element.getBoundingClientRect()
			const nextSize = Math.max(rect.width, rect.height)
			if (lastThumbnailSizeRef.current === nextSize) {
				return
			}
			lastThumbnailSizeRef.current = nextSize
			setThumbnailSize(nextSize)
		}

		updateThumbnailSize()

		let resizeObserver: ResizeObserver | null = null
		if (typeof ResizeObserver !== "undefined") {
			resizeObserver = new ResizeObserver(() => {
				updateThumbnailSize()
			})
			resizeObserver.observe(element)
		}
		return () => {
			resizeObserver?.disconnect()
		}
	}, [])

	const { url } = useThumbnailPreview({
		photo,
		project: props.project,
		previewSize: thumbnailSize && thumbnailSize > 250 ? PreviewSize.medium : PreviewSize.small,
	})

	const cullingTag =
		isCullingMode && (isSuggestingKeepersOrRejects || photo.manualCullingTag)
			? getCullingTag(photo)
			: null
	const shouldShowManualCullingTag = photo.manualCullingTag !== null && cullingTag !== null
	const isCullingProcessing =
		isCullingMode &&
		isSuggestingKeepersOrRejects &&
		photo.smartCullingTag === null &&
		!shouldShowManualCullingTag
	const isProcessing =
		(!isCullingMode && photo.status === "pending") ||
		photo.status === "applying" ||
		isCullingProcessing
	const isRejected = photo.flag === "rejected"

	const aiDebugBorder = useAtomValue(aiDebugBorderAtom)
	const colorTag = photo.colorTag ?? "No Color" // i18n-ignore: ColorTag enum value
	const backgroundClasses =
		thumbnailBackgroundClassesByColorTag[colorTag] ??
		thumbnailBackgroundClassesByColorTag["No Color"] // i18n-ignore: ColorTag fallback

	const groupedPhotoInfo = clusterPhotoGroupInfoMap[photo.id]
	const isGroupedGridPhoto =
		isCullingMode &&
		mode === "grid" &&
		groupSimilarPhotos &&
		!!groupedPhotoInfo &&
		groupedPhotoInfo.total > 1
	const isGroupedGridSelected =
		isGroupedGridPhoto && selectedGroupedClusterLeaderIds.has(groupedPhotoInfo.clusterLeaderId)
	const isGroupedGridHovered =
		isGroupedGridPhoto && hoveredGroupedClusterId === groupedPhotoInfo.clusterLeaderId
	const groupedSequenceLabel = isGroupedGridPhoto
		? `${groupedPhotoInfo.index + 1}/${groupedPhotoInfo.total}`
		: undefined

	useEffect(() => {
		if (!isGroupedGridPhoto) {
			return
		}

		const image = imageRef.current
		if (!image?.complete || !image.naturalWidth || !image.naturalHeight) {
			setImageNaturalSize(null)
			return
		}

		setImageNaturalSize({
			width: image.naturalWidth,
			height: image.naturalHeight,
		})
	}, [isGroupedGridPhoto, url])

	const computeThumbnailBackgroundClass = () => {
		const isSelectedBackground = isGroupedGridSelected || isSelected
		const isHoveredBackground = !isSelectedBackground && (isGroupedGridHovered || isHovering)

		if (isSelectedBackground) {
			return backgroundClasses.selected
		}
		if (isHoveredBackground) {
			return backgroundClasses.hover
		}
		return backgroundClasses.base
	}

	const thumbnailBackgroundClass = computeThumbnailBackgroundClass()
	const shouldShowInlineControls = isHovering || isMenuOpen || isGroupedGridSelected
	const imageTopInset = props.size === "small" ? 24 : 32
	const imageBottomInset = props.size === "small" ? 16 : 18
	const imageContainerHeight = thumbnailSize
		? Math.max(thumbnailSize - imageTopInset - imageBottomInset, 0)
		: null
	const imageContainerWidth = thumbnailSize
	const groupedSequenceLabelPosition =
		imageContainerWidth &&
		imageContainerHeight &&
		imageNaturalSize &&
		imageNaturalSize.width > 0 &&
		imageNaturalSize.height > 0
			? (() => {
					const containerAspectRatio = imageContainerWidth / imageContainerHeight
					const imageAspectRatio = imageNaturalSize.width / imageNaturalSize.height

					if (imageAspectRatio > containerAspectRatio) {
						const renderedHeight = imageContainerWidth / imageAspectRatio
						return {
							right: 0,
							top: `${(imageContainerHeight - renderedHeight) / 2}px`,
						}
					}

					return {
						right: 0,
						top: 0,
					}
				})()
			: {
					right: 0,
					top: 0,
				}

	const renderStatusIcon = () => {
		const size = props.size === "small" ? 10 : 14

		if (photo.isHidden) {
			return (
				<Icon
					name="hidden"
					size={size}
					onClick={(e) => {
						e.stopPropagation()
						void unhidePhotos([photo.id])
					}}
				/>
			)
		}

		if (photo.isUnsupported) {
			return <Icon name="unsupported" className="fill-yellow-500" size={size} />
		}

		if (shouldShowManualCullingTag) {
			return <MemoizedCullingTagIconSetter photo={photo} tag={cullingTag} size={14} />
		}

		if (isProcessing) {
			return <Spinner size={size} />
		}

		if (cullingTag) {
			return <MemoizedCullingTagIconSetter photo={photo} tag={cullingTag} size={14} />
		}

		if (!isCullingMode) {
			switch (photo.status) {
				case "error":
					return <Icon name="error" className="shrink-0 fill-yellow-500" size={size} />
				case "none":
					if (!isManualMode) {
						switch (photo.kind) {
							case "reference":
								return <Icon name="diamond" className="shrink-0" size={size} />
							case "locked":
								return <Icon name="locked" className="shrink-0" size={size} />
							case "default":
								if (photo.styleId) {
									return (
										<Icon
											name="check_circle"
											className="shrink-0 fill-green-500"
											size={size}
										/>
									)
								} else if (photo.isAnchorImage) {
									return (
										<Icon
											name="suggested"
											className="shrink-0 fill-accent-600"
											size={size}
										/>
									)
								}
						}
					}
			}
		}
	}

	const handleTap = (e: React.MouseEvent) => {
		e.stopPropagation()
		const clickEvent = {
			shiftKey: e.shiftKey,
			ctrlKey: e.ctrlKey,
			metaKey: e.metaKey,
		}

		if (singleTapTimeout) {
			clearTimeout(singleTapTimeout)
		}

		setSingleTapTimeout(
			setTimeout(() => {
				const { isShiftPressed } = selectThumbnailPhotoFromClick({
					photo: props.photo,
					event: clickEvent,
					isStackedPhoto: props.isStackedPhoto ?? false,
				})

				if (mode !== "grid" && !isShiftPressed) {
					setIsRatingBarActive(true)
				}

				if (!isCullingMode && mode === "grid") {
					const nextCurrentPhoto = store.get(currentPhotoAtom)
					if (nextCurrentPhoto?.id === props.photo.id) {
						void syncAdjustmentHistory(nextCurrentPhoto)
					}
				} else if (!isCurrentFileLoaded()) {
					void loadCurrentFile({ kinds: ["preview", "full"] })
				}

				analytics.trackEvent(
					{ label: "select_photo", type: "click", value: props.photo.id },
					e.target,
				)
			}),
		)
	}

	const handleDoubleTap = (e: React.MouseEvent) => {
		if (singleTapTimeout) {
			clearTimeout(singleTapTimeout)
			setSingleTapTimeout(null)
		}

		selectPhoto(props.photo, { fromClick: true })

		if (mode !== "grid" && !e.shiftKey) {
			setIsRatingBarActive(true)
		}

		if (mode === "grid") {
			openFocusView()
		}

		analytics.trackEvent(
			{ label: "open_photo", type: "click", clickType: "double", value: props.photo.id },
			e.target,
		)
	}

	const getBorderColorClass = (color: string) => {
		switch (color) {
			case "red":
				return "border-red-500"
			case "yellow":
				return "border-yellow-500"
			case "orange":
				return "border-orange-500"
			case "green":
				return "border-green-500"
			default:
				return "border-white"
		}
	}

	const getMaxFilenameLength = () => {
		if (!thumbnailSize) {
			return undefined
		}

		const reservedSpace = props.size === "small" ? 23 : 30 //statusIcon + padding
		const approximateChars = Math.floor((thumbnailSize - reservedSpace) / 6)
		return Math.max(10, Math.min(32, approximateChars))
	}
	const maxFilenameLength = getMaxFilenameLength()

	const borderColor = isCullingMode || isManualMode ? undefined : aiDebugBorder[photo.id]

	return (
		<div
			ref={thumbnailRef}
			role="gridcell"
			data-testid="photo_thumbnail"
			aria-label={photo.fileName ?? photo.id}
			aria-selected={isSelected}
			aria-busy={isProcessing}
			id={photo.id}
			className={clsx(
				"outline",
				isSelected ? "outline-2" : "outline-0",
				isPrimarySelection ? "outline-base-200" : "outline-base-500 dark:outline-base-400",
				borderColor && ["border-2", getBorderColorClass(borderColor)],
				thumbnailBackgroundClass,
				"relative flex aspect-square flex-col",
			)}
			onPointerEnter={() => {
				setIsHovering(true)
				if (isGroupedGridPhoto) {
					setHoveredGroupedClusterId(groupedPhotoInfo.clusterLeaderId)
				}
				if (shouldHandleRatingBar) {
					setIsRatingBarActive(true)
				}
			}}
			onPointerLeave={() => {
				setIsHovering(false)
				if (
					isGroupedGridPhoto &&
					hoveredGroupedClusterId === groupedPhotoInfo.clusterLeaderId
				) {
					setHoveredGroupedClusterId(null)
				}
				if (shouldHandleRatingBar) {
					setIsRatingBarActive(false)
				}
			}}
			onClick={handleTap}
			onDoubleClick={handleDoubleTap}
			onContextMenu={(e) => {
				analytics.trackEvent(
					{
						label: "open_photo_menu",
						type: "click",
						clickType: "right_click",
						value: props.photo.id,
					},
					e.target,
				)
				showContextMenu(e, <PhotoContextMenu photo={photo} />)
			}}
		>
			<div
				className="absolute inset-0"
				style={{
					top: imageTopInset,
					bottom: imageBottomInset,
				}}
			>
				{url ? (
					<img
						ref={imageRef}
						className="size-full object-contain object-center"
						src={url}
						onLoad={(event) => {
							if (!isGroupedGridPhoto) {
								return
							}
							const image = event.currentTarget
							setImageNaturalSize({
								width: image.naturalWidth,
								height: image.naturalHeight,
							})
						}}
					/>
				) : (
					<div className="absolute inset-0 flex items-center justify-center opacity-25">
						<Icon name={"photo"} size={20} />
					</div>
				)}
				{isGroupedGridPhoto && (
					<div
						className="pointer-events-none absolute bg-base-900/80 px-1 py-0.5 text-xxs font-semibold leading-none text-base-100"
						style={groupedSequenceLabelPosition}
					>
						{groupedSequenceLabel}
					</div>
				)}
			</div>

			{isRejected && (
				<div className="pointer-events-none absolute inset-0 z-[1] bg-base-900/70" />
			)}

			<div className={clsx("absolute inset-0 z-[2] flex flex-col justify-between")}>
				{isProcessing && <div className="absolute inset-0 bg-base-900/80" />}

				<div
					className={clsx(
						props.size === "small" ? "p-1 text-xxs" : "p-2 text-xs",
						"flex grow-0 flex-row items-center justify-between overflow-hidden",
					)}
					style={{
						textShadow: "1px 1px 3px rgb(0 0 0 / 25%)",
					}}
				>
					<div className="text-base-300">
						{maxFilenameLength
							? middleTruncate(props.photo.fileName, maxFilenameLength)
							: props.photo.fileName}
					</div>
					<div className="shrink-0">{renderStatusIcon()}</div>
				</div>
				<div className="mx-1 flex flex-row items-center justify-between">
					{photo && (
						<RatingStarsView
							count={photo.rating ?? 0}
							size={props.size}
							isRating={shouldShowInlineControls}
							onRate={(rating) => {
								if (isSelected) {
									rateSelectedPhotos(rating)
								} else {
									ratePhoto(rating, photo)
								}
							}}
						/>
					)}

					<div className="flex flex-row items-center space-x-2">
						<MemoizedThumbnailColorSetter
							size={props.size}
							photo={photo}
							isSelected={isSelected}
							isHovering={isHovering}
							isMenuOpen={isMenuOpen}
							shouldShowInlineControls={shouldShowInlineControls}
							setIsMenuOpen={setIsMenuOpen}
						/>
						<MemoizedThumbnailFlag
							size={props.size}
							photo={photo}
							isHovering={isHovering}
							isMenuOpen={isMenuOpen}
							shouldShowInlineControls={shouldShowInlineControls}
							setIsMenuOpen={setIsMenuOpen}
						/>
					</div>
				</div>
			</div>
		</div>
	)
}

export const ClusterThumbnail = (props: {
	coverPhoto: PhotoRecord
	cluster: ClusterInfo
	project: ProjectItem
}) => {
	const { t } = useTranslation("components")
	const cluster = props.cluster
	const coverPhoto = props.coverPhoto
	const mode = useAtomValue(photosViewAtom)
	const photosById = useAtomValue(photosAtom)

	const currentCluster = useAtomValue(currentClusterAtom)
	const isShowingKeepers = useAtomValue(isSuggestingKeepersAtom)
	const selectedPhotos = useAtomValue(selectedPhotosIDsAtom)

	const isAllPhotosSelected = cluster.clusterPhotoIds.every((id) => selectedPhotos.has(id))
	const isSelected =
		isAllPhotosSelected || currentCluster?.clusterLeaderId === cluster.clusterLeaderId

	const isRated = cluster.ratedPhotoCount > 0
	const hasKeepers = isShowingKeepers && cluster.keeperPhotoCount > 0
	const shouldShowClusterSize = cluster.clusterPhotoIds.length > 1
	const areAllClusterPhotosRejected = cluster.clusterPhotoIds.every(
		(id) => photosById[id]?.flag === "rejected",
	)

	const { url } = useThumbnailPreview({
		photo: coverPhoto,
		project: props.project,
		previewSize: PreviewSize.small,
	})

	const handleTap = (e: React.MouseEvent) => {
		e.stopPropagation()

		selectCluster(cluster, true)
		if (mode !== "grid" && !isCurrentFileLoaded()) {
			void loadCurrentFile({ kinds: ["preview", "full"] })
		}
		analytics.trackEvent(
			{ label: "select_cluster", type: "click", value: cluster.coverPhotoId },
			e.target,
		)
	}

	const renderClusterSize = () => {
		return (
			<div className="flex flex-row space-x-0.5 text-xxs">
				<Icon name={"photo"} size={12} />
				<div>{cluster.clusterPhotoIds.length}</div>
			</div>
		)
	}

	const renderClusterIsRated = () => {
		if (isRated) {
			return (
				<div className="flex flex-row space-x-0.5 text-xxs">
					<Icon name={"rated"} size={12} />
					<div>{cluster.ratedPhotoCount}</div>
				</div>
			)
		}
	}

	const renderClusterHasKeepers = () => {
		if (hasKeepers) {
			return (
				<div className="flex flex-row items-center space-x-0.5 text-xxs">
					<KeeperIcon size={10} />
					<div>{cluster.keeperPhotoCount}</div>
				</div>
			)
		}
	}

	return (
		<div
			aria-label={t("thumbnail.ariaClusterPhotos", { count: cluster.clusterPhotoIds.length })}
			aria-selected={isSelected}
			data-testid={`cluster_thumbnail_${cluster.clusterLeaderId}`}
			id={cluster.clusterLeaderId}
			className={clsx(
				isSelected
					? "outline outline-2 outline-base-500 dark:outline-base-400"
					: "outline-0",
				"relative flex aspect-square flex-col bg-base-900",
			)}
			onClick={handleTap}
		>
			{url ? (
				<div className={clsx("absolute inset-0")}>
					<img className="size-full object-contain" src={url} />
				</div>
			) : (
				<div className="absolute inset-0 flex items-center justify-center opacity-25">
					<Icon name={"photo"} size={20} />
				</div>
			)}
			{areAllClusterPhotosRejected && (
				<div className="pointer-events-none absolute inset-0 z-[1] bg-base-900/70" />
			)}

			<div className={clsx("absolute inset-0 z-[2] flex flex-col justify-between")}>
				{shouldShowClusterSize && (
					<div
						className={clsx(
							"flex w-fit flex-row items-center justify-start space-x-1 overflow-hidden bg-base-700 px-1 py-0.5 text-xxs font-bold",
						)}
					>
						{renderClusterSize()}
					</div>
				)}
				<div
					className={clsx(
						"mt-auto flex w-fit flex-row items-center justify-end space-x-1 overflow-hidden px-1 py-0.5 text-xxs font-bold",
						isRated || hasKeepers ? "bg-base-700" : "bg-base-900",
					)}
				>
					{renderClusterIsRated()}
					{renderClusterHasKeepers()}
				</div>
			</div>
		</div>
	)
}

export const StarsView = (props: { count: number; size: number; showAllStars?: boolean }) => {
	return (
		<div className="flex flex-row space-x-0" data-testid={`${props.count}_stars`}>
			{Array(props.showAllStars ? 5 : props.count)
				.fill(0)
				.map((_, index) => (
					<Icon
						key={index}
						name={index < props.count ? "star_fill" : "star"}
						size={props.size}
					></Icon>
				))}
		</div>
	)
}

export const RatingStarsView = (props: {
	count: number
	size: ThumbnailSize
	isRating: boolean
	onRate: (rating: Rating) => void
}) => {
	const [currentHoverIndex, setCurrentHoverIndex] = useState(-1)

	const starSize = props.size === "small" ? 8 : 12
	const starOffset = props.size === "small" ? "m-[-3px]" : "m-[-4px]"
	const ratingDotSize = props.size === "small" ? "size-0.5" : "size-1"
	const ratingDotSpacing = props.size === "small" ? "space-x-1.5" : "space-x-2"

	const starIndex = () => {
		if (currentHoverIndex !== -1) {
			return currentHoverIndex
		} else {
			return props.count - 1
		}
	}

	const renderStar = (rating: Rating, index: number) => {
		return (
			<div key={`photo-star-${index}`} onPointerEnter={() => setCurrentHoverIndex(index)}>
				<Icon
					className={clsx(
						index <= starIndex() ? "visible" : "invisible",
						props.isRating ? "fill-base-100" : "fill-base-300",
					)}
					name={"star_fill"}
					size={starSize}
					onClick={() => {
						if (rating === props.count) {
							props.onRate?.(0)
						} else {
							props.onRate?.(rating)
						}
					}}
				></Icon>
			</div>
		)
	}

	return (
		<div
			className={clsx(props.size === "small" ? "mb-0.5" : "mb-1", "relative mx-1")}
			data-testid="photo_status"
			onPointerDown={(e) => e.stopPropagation()}
			onClick={(e) => e.stopPropagation()}
			onDoubleClick={(e) => e.stopPropagation()}
		>
			<div
				className={clsx(
					props.isRating ? "visible" : "invisible",
					ratingDotSpacing,
					"absolute z-10 flex flex-row items-center",
				)}
			>
				{Array(5)
					.fill(0)
					.map((_, index) => (
						<div
							key={index}
							className={clsx(
								ratingDotSize,
								"inset-0 justify-center self-center rounded-full bg-base-300",
							)}
						></div>
					))}
			</div>
			<div
				className={clsx(starOffset, "absolute inset-0 z-10 flex flex-row space-x-0")}
				data-testid={`${props.count}_stars`}
				onPointerLeave={() => setCurrentHoverIndex(-1)}
			>
				{RatingValues.filter((rating) => rating > 0).map(renderStar)}
			</div>
		</div>
	)
}

export const MemoizedThumbnail = React.memo(Thumbnail)
